import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaCheck, FaCopy } from "react-icons/fa";
import { useDocsCtx } from "./DocsCtx";

/**
 * Persistent credential picker shown at the top of the docs layout.
 * Lets the user explicitly choose which API key and session to use
 * in code samples and the Try-It sandbox.
 */
function AuthBanner() {
  const {
    apiKey,
    keys,
    selectedKeyId,
    setSelectedKeyId,
    sessions,
    selectedSessionId,
    setSelectedSessionId,
    activeWorkspace,
    workspaceId,
    loading,
  } = useDocsCtx();
  const [workspaceCopied, setWorkspaceCopied] = useState(false);

  const maskKey = (key) => {
    if (!key || key.length < 12) return key || "";
    return key.slice(0, 6) + "\u2026" + key.slice(-6);
  };

  const copyWorkspaceId = () => {
    if (!workspaceId) return;
    navigator.clipboard?.writeText(workspaceId);
    setWorkspaceCopied(true);
    setTimeout(() => setWorkspaceCopied(false), 2000);
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        {/* API Key selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="docs-api-key" className="font-semibold whitespace-nowrap">
            API Key:
          </label>
          {loading ? (
            <span className="text-xs text-slate-400">Loading...</span>
          ) : keys.length === 0 ? (
            <span className="text-xs text-slate-500">
              No keys found.{" "}
              <Link to="/settings/api-keys" className="underline hover:text-slate-900">
                Create one
              </Link>
            </span>
          ) : (
            <select
              id="docs-api-key"
              value={selectedKeyId}
              onChange={(e) => setSelectedKeyId(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
            >
              <option value="">-- Select an API key --</option>
              {keys.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name ? `${k.name} (${maskKey(k.key)})` : maskKey(k.key)}
                </option>
              ))}
            </select>
          )}
          {apiKey && (
            <code className="hidden sm:inline rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 font-mono">
              {maskKey(apiKey)}
            </code>
          )}
        </div>

        {/* Workspace/client ID */}
        <div className="flex items-center gap-2 min-w-0">
          <label className="font-semibold whitespace-nowrap">
            Workspace:
          </label>
          <div className="min-w-0">
            <p className="text-xs text-slate-600 truncate max-w-[180px]">
              {activeWorkspace?.name || "Active workspace"}
            </p>
            <code className="block rounded bg-white px-1.5 py-0.5 text-xs text-slate-600 font-mono truncate max-w-[220px] border border-slate-200">
              {workspaceId || "loading"}
            </code>
          </div>
          <button
            type="button"
            onClick={copyWorkspaceId}
            disabled={!workspaceId}
            className="rounded border border-slate-300 bg-white p-1.5 text-slate-500 hover:text-slate-900 disabled:opacity-50"
            title="Copy workspace ID"
          >
            {workspaceCopied ? <FaCheck size={10} /> : <FaCopy size={10} />}
          </button>
        </div>

        {/* Session selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="docs-session" className="font-semibold whitespace-nowrap">
            Session:
          </label>
          {loading ? (
            <span className="text-xs text-slate-400">Loading...</span>
          ) : sessions.length === 0 ? (
            <span className="text-xs text-slate-500">No sessions connected</span>
          ) : (
            <select
              id="docs-session"
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-400"
            >
              <option value="">-- Select a session --</option>
              {sessions.map((s) => {
                const id = s.sessionId || s.id;
                const status = s.status === "connected" ? "connected" : "disconnected";
                return (
                  <option key={id} value={id}>
                    {id} ({status})
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Links */}
        <div className="flex items-center gap-3 text-xs ml-auto">
          <Link to="/settings/api-keys" className="text-sky-600 hover:text-sky-800 underline">
            Manage keys
          </Link>
          <Link to="/integration-and-api/try-it" className="text-sky-600 hover:text-sky-800 underline">
            Open sandbox
          </Link>
        </div>
      </div>

      {!apiKey && !loading && keys.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          Select an API key above to see your real credentials in the code samples below.
        </p>
      )}
    </div>
  );
}

export default AuthBanner;
